import {
  acknowledgmentSchema,
  recordSchema,
  jsonBytes,
  LIMITS,
  type PlayRecord,
  type SessionMetadata,
} from "@slime/contracts";

interface Entry {
  record: PlayRecord;
  bytes: number;
  tries: number;
  enqueuedAt: number;
}
interface TransportOptions {
  fetch?: typeof fetch;
  now?: () => number;
  random?: () => number;
  onDrop?: (seq: number) => void;
  onStop?: () => void;
  /** 记录被接受入队时回调。仅表示本地接受，不代表后端已经确认。 */
  onAccept?: (record: PlayRecord) => void;
  maxBytes?: number;
  maxRecords?: number;
  minUploadIntervalMs?: number;
  maxUploadIntervalMs?: number;
  flushThresholdBytes?: number;
  /** Keep records in memory until the server-provided policy is known. */
  deferred?: boolean;
}
/** Independent bounded queue. Calls never wait for networking on the game path. */
export class RecordTransport {
  private queue: Entry[] = [];
  private bytes = 0;
  private inFlight = false;
  private nextAt = 0;
  private lastRequestAt = Number.NEGATIVE_INFINITY;
  private batchLimit = LIMITS.batchBytes as number;
  private stopped = false;
  private active: boolean;
  /** A lifecycle request bypasses normal pacing and selects control records ahead of detail backlog. */
  private criticalRequested = false;
  private controller?: AbortController;
  readonly diagnostics = {
    dropped: 0,
    failures: 0,
    acknowledged: 0,
    peakBytes: 0,
  };
  constructor(
    readonly endpoint: string,
    readonly metadata: SessionMetadata,
    private readonly options: TransportOptions = {},
  ) {
    this.active = options.deferred !== true;
  }
  get pendingCount() {
    return this.queue.length;
  }
  get bufferedBytes() {
    return this.bytes;
  }
  enqueue(record: PlayRecord): void {
    try {
      this.accept(record);
    } catch {
      this.diagnostics.failures++;
      this.stop();
    }
  }
  private accept(record: PlayRecord): void {
    if (this.stopped) {
      this.drop(record.seq);
      return;
    }
    // Validate and reserve exact bytes before retaining an immutable copy.
    const validated = recordSchema.safeParse(record);
    if (!validated.success) {
      this.drop(record.seq);
      return;
    }
    const bytes = jsonBytes(record);
    if (bytes > LIMITS.recordBytes) {
      this.drop(record.seq);
      return;
    }
    const max = this.options.maxBytes ?? 8 * 1024 * 1024;
    const limit = this.options.maxRecords ?? 4096;
    const closure =
      record.kind === "event" && record.data.name === "recording_closed";
    const critical =
      (record.kind === "snapshot" && record.data.reason !== "periodic") ||
      (record.kind === "event" &&
        [
          "session_started",
          "attempt_started",
          "attempt_ended",
          "paused",
          "resumed",
          "feedback_submitted",
        ].includes(record.data.name));
    // Reserve space even when dense entity facts fill the queue; never evict in-flight records.
    const reserveBytes = closure
      ? 0
      : Math.min(critical ? 16 * 1024 : 64 * 1024, max / 8);
    const reserveRecords = closure
      ? 0
      : Math.min(critical ? 8 : 32, Math.floor(limit / 8));
    if (
      this.bytes + bytes > max - reserveBytes ||
      this.queue.length >= limit - reserveRecords
    ) {
      this.drop(record.seq);
      return;
    }
    this.queue.push({
      record: structuredClone(record),
      bytes,
      tries: 0,
      enqueuedAt: (this.options.now ?? Date.now)(),
    });
    this.bytes += bytes;
    this.diagnostics.peakBytes = Math.max(
      this.diagnostics.peakBytes,
      this.bytes,
    );
    this.options.onAccept?.(record);
  }
  private drop(seq: number) {
    this.diagnostics.dropped++;
    this.options.onDrop?.(seq);
  }
  private remove(entries: Entry[], dropped: boolean) {
    const selected = new Set(entries);
    this.queue = this.queue.filter((e) => {
      if (!selected.has(e)) return true;
      this.bytes -= e.bytes;
      if (dropped && e.tries === 0) this.drop(e.record.seq);
      return false;
    });
  }
  stop(): void {
    this.stopped = true;
    this.controller?.abort();
    this.remove([...this.queue], true);
    this.options.onStop?.();
  }
  /** Discard a provisional queue without reporting policy-driven removal as data loss. */
  abandon(): void {
    this.stopped = true;
    this.controller?.abort();
    this.queue = [];
    this.bytes = 0;
  }
  /** Rewrite records only while networking is still deferred. */
  rewriteBuffered(
    transform: (record: PlayRecord) => PlayRecord | undefined,
  ): PlayRecord[] {
    if (this.active || this.inFlight) throw new Error("Transport is active");
    const rewritten: Entry[] = [];
    for (const entry of this.queue) {
      const record = transform(structuredClone(entry.record));
      if (!record) continue;
      const validated = recordSchema.safeParse(record);
      if (!validated.success) throw new Error("Invalid rewritten record");
      rewritten.push({ ...entry, record, bytes: jsonBytes(record) });
    }
    this.queue = rewritten;
    this.bytes = rewritten.reduce((total, entry) => total + entry.bytes, 0);
    return rewritten.map((entry) => entry.record);
  }
  activate(options: {
    snapshots: boolean;
    presentation: SessionMetadata["presentation"];
    minUploadIntervalMs: number;
    maxUploadIntervalMs: number;
    flushThresholdBytes: number;
  }): void {
    if (this.stopped) return;
    this.metadata.capabilities.snapshots = options.snapshots;
    this.metadata.presentation = options.presentation;
    this.options.minUploadIntervalMs = options.minUploadIntervalMs;
    this.options.maxUploadIntervalMs = options.maxUploadIntervalMs;
    this.options.flushThresholdBytes = options.flushThresholdBytes;
    this.active = true;
  }
  async flush(keepalive = false): Promise<void> {
    try {
      await this.send(keepalive);
    } catch {
      this.diagnostics.failures++;
      this.stop();
    }
  }
  flushCritical(): Promise<void> {
    this.criticalRequested = true;
    return this.flush(true);
  }
  private critical(record: PlayRecord): boolean {
    return (
      record.kind === "event" &&
      record.data.type === "system" &&
      [
        "session_started",
        "attempt_started",
        "attempt_ended",
        "paused",
        "resumed",
        "feedback_submitted",
        "recording_closed",
      ].includes(record.data.name)
    );
  }
  private async send(keepalive: boolean): Promise<void> {
    const now = this.options.now ?? Date.now;
    const time = now();
    if (
      this.stopped ||
      !this.active ||
      this.inFlight ||
      !this.queue.length ||
      time < this.nextAt
    )
      return;
    const critical = this.criticalRequested &&
      this.queue.some((entry) => this.critical(entry.record));
    if (!critical) this.criticalRequested = false;
    if (!keepalive && !critical) {
      const threshold = this.options.flushThresholdBytes ?? 0;
      const maxInterval = this.options.maxUploadIntervalMs ?? 0;
      const minInterval = this.options.minUploadIntervalMs ?? 0;
      const thresholdReached = this.bytes >= threshold;
      const oldestExpired = time >= this.queue[0].enqueuedAt + maxInterval;
      if (
        (!thresholdReached && !oldestExpired) ||
        time < this.lastRequestAt + minInterval
      )
        return;
    }
    // keepalive payloads stay comfortably below browser aggregate limits.
    const cap = Math.min(
      this.batchLimit,
      keepalive ? 48 * 1024 : this.batchLimit,
    );
    const entries: Entry[] = [];
    let bytes = jsonBytes({ ...this.metadata, records: [] });
    const candidates = critical
      ? this.queue.filter((entry) => this.critical(entry.record))
      : [...this.queue];
    for (const entry of candidates) {
      const actualBytes = jsonBytes(entry.record);
      if (actualBytes > LIMITS.recordBytes) {
        this.remove([entry], true);
        continue;
      }
      if (
        entries.length === LIMITS.batchRecords ||
        bytes + actualBytes + 1 > cap
      )
        break;
      entries.push(entry);
      bytes += actualBytes + 1;
    }
    if (!entries.length) {
      if (!keepalive && this.queue[0]) this.remove([this.queue[0]], true);
      return;
    }
    this.inFlight = true;
    this.lastRequestAt = time;
    this.controller = new AbortController();
    const timer = setTimeout(() => this.controller?.abort(), LIMITS.requestMs);
    for (const entry of entries) entry.tries++;
    try {
      const response = await (this.options.fetch ?? fetch)(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          ...this.metadata,
          records: entries.map((e) => e.record),
        }),
        signal: this.controller.signal,
        keepalive,
      });
      if (response.ok) {
        const parsed = acknowledgmentSchema.safeParse(await response.json());
        if (!parsed.success) throw new Error("Invalid acknowledgment");
        const ack = new Set(parsed.data.acknowledgedRecordIds);
        const confirmed = entries.filter((e) => ack.has(e.record.recordId));
        this.remove(confirmed, false);
        this.diagnostics.acknowledged += confirmed.length;
        if (confirmed.length !== entries.length)
          throw new Error("Incomplete acknowledgment");
        this.nextAt = 0;
      } else {
        const error = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        if (
          response.status === 403 ||
          error?.error?.code === "SESSION_LIMIT_REACHED"
        ) {
          this.stop();
          return;
        }
        if (response.status === 413) {
          if (entries.length === 1) this.remove(entries, true);
          else this.batchLimit = Math.max(1024, Math.floor(bytes / 2));
          // Resizing does not spend a network retry budget.
          for (const entry of entries) entry.tries--;
          return;
        }
        if ([400, 409, 415].includes(response.status)) {
          this.remove(entries, true);
          return;
        }
        const retry = Number(response.headers.get("Retry-After"));
        if (response.status === 429 && Number.isFinite(retry) && retry > 0)
          this.nextAt = now() + retry * 1000;
        throw new Error("Upload failed");
      }
    } catch {
      if (this.stopped) return;
      this.diagnostics.failures++;
      this.remove(
        entries.filter((e) => e.tries >= 6),
        true,
      ); // initial request + five retries
      const exponent = Math.min(
        5,
        Math.max(...entries.map((e) => e.tries)) - 1,
      );
      this.nextAt = Math.max(
        this.nextAt,
        now() +
          Math.min(30_000, 1000 * 2 ** exponent) *
            (1 + (this.options.random ?? Math.random)() * 0.25),
      );
    } finally {
      clearTimeout(timer);
      this.controller = undefined;
      this.inFlight = false;
      this.criticalRequested = this.queue.some((entry) =>
        this.critical(entry.record),
      );
      if (
        this.criticalRequested &&
        !this.stopped &&
        (this.options.now ?? Date.now)() >= this.nextAt
      )
        void this.flush(true);
    }
  }
}
